import React, { useState } from 'react';
import { RefreshCw, MapPin, Phone, Globe, Star, Clock, ThumbsUp, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function GoogleEnrichment({ agentId, agent, onUpdate }) {
  const [refreshing, setRefreshing] = useState(false);
  const [profile, setProfile] = useState(null);

  const handleRefresh = async (forceRefresh = false) => {
    if (!agent.enable_google_enrichment) {
      toast.error('Please enable Google enrichment first');
      return;
    }

    if (!agent.business_name_public || !agent.business_address_query) {
      toast.error('Please provide business name and address');
      return;
    }

    setRefreshing(true);
    try {
      const response = await api.post(`/agents/${agentId}/refresh-google-profile`, {
        force_refresh: forceRefresh
      });
      setProfile({ ...response.data.profile, is_mocked: response.data.is_mocked });
      if (response.data.is_mocked) {
        toast.success('Profile loaded with MOCKED data (no API key configured)');
      } else {
        toast.success(forceRefresh ? 'Business profile updated with fresh data!' : 'Google Profile refreshed successfully!');
      }
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refresh Google Profile');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Switch
            checked={agent.enable_google_enrichment || false}
            onCheckedChange={(checked) => onUpdate({ enable_google_enrichment: checked })}
            data-testid="google-enrichment-toggle"
          />
          <Label className="text-sm font-medium">Enable Google Business Profile Enrichment</Label>
        </div>
      </div>

      {agent.enable_google_enrichment && (
        <>
          <div className="space-y-2">
            <Label htmlFor="business_name_public" className="text-sm font-medium">
              Business Name (as on Google)
            </Label>
            <Input
              id="business_name_public"
              placeholder="e.g., Acme Coffee Shop"
              value={agent.business_name_public || ''}
              onChange={(e) => onUpdate({ business_name_public: e.target.value })}
              className="h-11 rounded-xl"
              data-testid="business-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="business_address_query" className="text-sm font-medium">
              Address / City
            </Label>
            <Input
              id="business_address_query"
              placeholder="e.g., 123 Main St, San Francisco"
              value={agent.business_address_query || ''}
              onChange={(e) => onUpdate({ business_address_query: e.target.value })}
              className="h-11 rounded-xl"
              data-testid="address-query-input"
            />
            <p className="text-xs text-muted-foreground">
              Used to find your business on Google Maps
            </p>
          </div>

          {agent.google_place_id && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Google Place ID</Label>
              <Input
                value={agent.google_place_id}
                className="h-11 rounded-xl bg-muted"
                readOnly
                data-testid="place-id-display"
              />
              <p className="text-xs text-muted-foreground">
                Automatically detected and stored
              </p>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={() => handleRefresh(false)}
              disabled={refreshing || !agent.business_name_public || !agent.business_address_query}
              className="flex-1 rounded-full"
              data-testid="refresh-profile-button"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh Google Profile'}
            </Button>
            {agent.google_place_id && (
              <Button
                onClick={() => handleRefresh(true)}
                disabled={refreshing}
                variant="outline"
                className="rounded-full"
                title="Search for a new business (clears cached Place ID)"
                data-testid="force-refresh-button"
              >
                <RotateCcw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            )}
          </div>
          {agent.google_place_id && (
            <p className="text-xs text-muted-foreground text-center">
              Changed businesses? Click the ↺ button to search fresh
            </p>
          )}

          {profile && (
            <Card className={`border-2 ${profile.is_mocked ? 'border-amber-300 bg-amber-50/50' : 'border-green-200 bg-green-50/50'}`} data-testid="profile-preview">
              <CardHeader>
                <CardTitle className={`text-lg flex items-center ${profile.is_mocked ? 'text-amber-900' : 'text-green-900'}`}>
                  <MapPin className="w-5 h-5 mr-2" />
                  {profile.is_mocked ? 'MOCKED Profile Data' : 'Profile Loaded Successfully'}
                </CardTitle>
                <CardDescription className={profile.is_mocked ? 'text-amber-700' : 'text-green-700'}>
                  {profile.is_mocked && <span className="font-semibold">[No API key - using sample data] </span>}
                  Last refreshed: {new Date(profile.last_refreshed).toLocaleString()}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {profile.name && (
                  <div>
                    <strong className={profile.is_mocked ? 'text-amber-900' : 'text-green-900'}>Name:</strong> {profile.name}
                  </div>
                )}
                {profile.address && (
                  <div className="flex items-start">
                    <MapPin className={`w-4 h-4 mr-2 mt-0.5 ${profile.is_mocked ? 'text-amber-600' : 'text-green-600'}`} />
                    <span>{profile.address}</span>
                  </div>
                )}
                {profile.phone && (
                  <div className="flex items-center">
                    <Phone className={`w-4 h-4 mr-2 ${profile.is_mocked ? 'text-amber-600' : 'text-green-600'}`} />
                    <span>{profile.phone}</span>
                  </div>
                )}
                {profile.website && (
                  <div className="flex items-center">
                    <Globe className={`w-4 h-4 mr-2 ${profile.is_mocked ? 'text-amber-600' : 'text-green-600'}`} />
                    <a
                      href={profile.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {profile.website}
                    </a>
                  </div>
                )}
                {profile.rating && (
                  <div className="flex items-center">
                    <Star className="w-4 h-4 mr-2 text-yellow-500 fill-yellow-500" />
                    <span>
                      {profile.rating}/5 ({profile.total_ratings} reviews)
                    </span>
                  </div>
                )}
                {profile.themes && profile.themes.length > 0 && (
                  <div>
                    <div className="flex items-center mb-2">
                      <ThumbsUp className={`w-4 h-4 mr-2 ${profile.is_mocked ? 'text-amber-600' : 'text-green-600'}`} />
                      <strong className={profile.is_mocked ? 'text-amber-900' : 'text-green-900'}>Top Compliments:</strong>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {profile.themes.map((theme, i) => (
                        <Badge key={i} variant="secondary" className={profile.is_mocked ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}>
                          {theme}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {profile.is_mocked && (
                  <div className="mt-4 p-3 bg-amber-100 border border-amber-200 rounded-lg text-amber-800 text-xs">
                    <strong>Note:</strong> This is sample data. Add a <code>GOOGLE_MAPS_API_KEY</code> to your backend environment to fetch real business data.
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
            <p>
              <strong>How it works:</strong> We fetch your business info from Google Places
              and use it to answer customer questions about hours, location, contact info, etc.
              Data is cached and updated when you click "Refresh".
            </p>
          </div>
        </>
      )}
    </div>
  );
}
